import { useCallback, useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { InventoryGateway } from '../application/inventoryGateway';
import type { AuthenticatedIdentity } from '../domain/access';
import type { InventoryAuthority, InventoryCursor, InventoryFilters, InventoryItem } from '../domain/inventory';
import { RasterMap } from './RasterMap';
import type { RasterViewport } from './RasterMap';
import styles from './InventoryScreen.module.css';

interface InventoryScreenProps {
  readonly gateway: InventoryGateway;
  readonly navigateToAccess?: () => void;
  readonly navigateToDashboard?: () => void;
}
interface InventoryState {
  readonly identity: AuthenticatedIdentity | null;
  readonly items: readonly InventoryItem[];
  readonly totalCount: number;
  readonly nextCursor: InventoryCursor | null;
  readonly loading: boolean;
  readonly error: string | null;
}
type ViewMode='map'|'list';
type PageSize=10|20|30;

const initialFilters:InventoryFilters={query:'',sale:true,rent:true,active:true,reserved:true,draft:true,zone:null};
function defaultNavigateToAccess():void{window.location.replace('/');}
function defaultNavigateToDashboard():void{window.location.assign('/dashboard');}
function formatPrice(item:InventoryItem):string{return new Intl.NumberFormat('es-MX',{style:'currency',currency:item.currency,maximumFractionDigits:0}).format(item.price);}
function compactPrice(item:InventoryItem):string{
  const value=item.price;
  if(value>=1_000_000)return '$'+(value/1_000_000).toFixed(value>=10_000_000?0:1)+'M';
  if(value>=1_000)return '$'+String(Math.round(value/1_000))+'K';
  return '$'+String(Math.round(value));
}
function transactionLabel(value:InventoryItem['transactionType']):string{return value==='sale'?'En venta':'En renta';}
function propertyTypeLabel(value:string):string{return({house:'Casa',apartment:'Departamento',condo:'Condominio',land:'Terreno',office:'Oficina',retail:'Negocio',warehouse:'Bodega',industrial:'Industrial',housing:'Vivienda',room:'Habitación',business:'Negocio'} as Record<string,string>)[value]??value;}

export function InventoryScreen({gateway,navigateToAccess=defaultNavigateToAccess,navigateToDashboard=defaultNavigateToDashboard}:InventoryScreenProps):React.JSX.Element{
  const [filters,setFilters]=useState<InventoryFilters>(initialFilters);
  const [queryDraft,setQueryDraft]=useState('');
  const [view,setView]=useState<ViewMode>('map');
  const [pageSize,setPageSize]=useState<PageSize>(20);
  const [pageCursors,setPageCursors]=useState<readonly (InventoryCursor|null)[]>([null]);
  const [pageIndex,setPageIndex]=useState(0);
  const [selected,setSelected]=useState<InventoryItem|null>(null);
  const [authority,setAuthority]=useState<InventoryAuthority|null>(null);
  const [authorityLoading,setAuthorityLoading]=useState(false);
  const [requestOpen,setRequestOpen]=useState(false);
  const [authorizationDraft,setAuthorizationDraft]=useState('');
  const [authorizationBusy,setAuthorizationBusy]=useState(false);
  const [notice,setNotice]=useState<string|null>(null);
  const [mediaUrl,setMediaUrl]=useState<string|null>(null);
  const [uploadingMedia,setUploadingMedia]=useState(false);
  const [mapItems,setMapItems]=useState<readonly InventoryItem[]>([]);
  const [mapTotalCount,setMapTotalCount]=useState(0);
  const [mapLoading,setMapLoading]=useState(false);
  const [drawingZone,setDrawingZone]=useState(false);
  const mapAbortRef=useRef<AbortController|null>(null);
  const carouselRef=useRef<HTMLDivElement|null>(null);
  const [state,setState]=useState<InventoryState>({identity:null,items:[],totalCount:0,nextCursor:null,loading:true,error:null});

  useEffect(()=>{
    const controller=new AbortController();let active=true;
    void gateway.resolveCurrentIdentity(controller.signal).then(result=>{if(!active)return;if(!result.ok){navigateToAccess();return;}setState(current=>({...current,identity:result.identity}));}).catch((error:unknown)=>{if(!controller.signal.aborted&&!(error instanceof DOMException&&error.name==='AbortError'))navigateToAccess();});
    return()=>{active=false;controller.abort();};
  },[gateway,navigateToAccess]);

  useEffect(()=>{
    if(state.identity===null)return;
    const controller=new AbortController();let active=true;
    const cursor=pageCursors[pageIndex]??null;
    void gateway.loadInventory({...filters,cursor,limit:pageSize},controller.signal).then(result=>{
      if(!active)return;
      if(!result.ok){setState(current=>({...current,loading:false,error:result.message}));return;}
      setState(current=>({...current,loading:false,items:result.page.items,totalCount:result.page.totalCount,nextCursor:result.page.nextCursor,error:null}));
    }).catch((error:unknown)=>{if(!controller.signal.aborted&&!(error instanceof DOMException&&error.name==='AbortError'))setState(current=>({...current,loading:false,error:'No fue posible cargar el Inventario MLS.'}));});
    return()=>{active=false;controller.abort();};
  },[filters,gateway,pageCursors,pageIndex,pageSize,state.identity]);

  useEffect(()=>{
    if(selected?.mediaPath===null||selected?.mediaPath===undefined)return;
    const controller=new AbortController();let active=true;let objectUrl:string|null=null;
    void gateway.loadMediaObject(selected.mediaPath,controller.signal).then(result=>{if(!active||!result.ok)return;objectUrl=result.objectUrl;setMediaUrl(result.objectUrl);}).catch(()=>setMediaUrl(null));
    return()=>{active=false;controller.abort();if(objectUrl!==null)URL.revokeObjectURL(objectUrl);};
  },[gateway,selected?.mediaPath]);

  useEffect(()=>{
    if(selected===null)return;
    const controller=new AbortController();let active=true;
    void gateway.loadAuthority(selected.listingId,controller.signal).then(result=>{
      if(!active)return;
      setAuthorityLoading(false);
      if(!result.ok){setState(current=>({...current,error:result.message}));return;}
      setAuthority(result.authority);
    }).catch((error:unknown)=>{
      if(!controller.signal.aborted&&!(error instanceof DOMException&&error.name==='AbortError')){
        setAuthorityLoading(false);
        setState(current=>({...current,error:'No fue posible verificar la autoridad de esta propiedad.'}));
      }
    });
    return()=>{active=false;controller.abort();};
  },[gateway,selected]);

  useEffect(()=>()=>mapAbortRef.current?.abort(),[]);

  useEffect(()=>{
    if(view!=='map'||selected===null||carouselRef.current===null)return;
    const target=carouselRef.current.querySelector<HTMLElement>(
      '[data-listing-id="'+selected.listingId+'"]',
    );
    if(target!==null&&typeof target.scrollIntoView==='function'){
      target.scrollIntoView({behavior:'smooth',inline:'center',block:'nearest'});
    }
  },[selected,view]);

  const handleViewportChange=useCallback((viewport:RasterViewport):void=>{
    if(state.identity===null)return;
    mapAbortRef.current?.abort();
    const controller=new AbortController();
    mapAbortRef.current=controller;
    setMapLoading(true);
    const limit=viewport.zoom>=15?40:viewport.zoom>=13?80:120;
    void gateway.loadMapInventory({
      ...filters,
      west:viewport.west,
      south:viewport.south,
      east:viewport.east,
      north:viewport.north,
      limit,
    },controller.signal).then(result=>{
      if(controller.signal.aborted)return;
      if(!result.ok){setMapLoading(false);setState(current=>({...current,error:result.message}));return;}
      setMapItems(result.page.items);
      setMapTotalCount(result.page.totalCount);
      setMapLoading(false);
    }).catch((error:unknown)=>{
      if(controller.signal.aborted||error instanceof DOMException&&error.name==='AbortError')return;
      setMapLoading(false);
      setState(current=>({...current,error:'No fue posible actualizar el mapa para esta zona.'}));
    });
  },[filters,gateway,state.identity]);

  const mapSource=mapItems.length>0||!mapLoading?mapItems:state.items;
  const mapPoints=mapSource.flatMap(item=>item.latitude===null||item.longitude===null?[]:[{
    id:item.listingId,
    latitude:item.latitude,
    longitude:item.longitude,
    label:compactPrice(item),
    selected:selected?.listingId===item.listingId,
  }]);

  function resetPaging():void{setState(current=>({...current,loading:true,error:null}));setPageCursors([null]);setPageIndex(0);setSelected(null);}
  function toggleTransaction(kind:'sale'|'rent'):void{
    setFilters(current=>{const next={...current,[kind]:!current[kind]};if(!next.sale&&!next.rent)return current;return next;});resetPaging();
  }
  function toggleStatus(kind:'active'|'reserved'|'draft'):void{
    setFilters(current=>{
      const next={...current,[kind]:!current[kind]};
      if(!next.active&&!next.reserved&&!next.draft)return current;
      return next;
    });
    resetPaging();
  }
  function applySearch(event:SyntheticEvent<HTMLFormElement>):void{event.preventDefault();setFilters(current=>({...current,query:queryDraft.trim()}));resetPaging();}
  function finishZone(points:readonly {readonly latitude:number;readonly longitude:number}[]):void{
    setFilters(current=>({...current,zone:points}));
    setDrawingZone(false);
    resetPaging();
    setNotice('Zona aplicada al mapa y a la lista mediante filtro geográfico del servidor.');
  }
  function clearZone():void{
    setFilters(current=>({...current,zone:null}));
    setDrawingZone(false);
    resetPaging();
    setNotice('Zona eliminada.');
  }
  function changePageSize(value:PageSize):void{setPageSize(value);resetPaging();}
  function nextPage():void{
    if(state.nextCursor===null)return;
    setState(current=>({...current,loading:true,error:null}));
    const nextIndex=pageIndex+1;
    setPageCursors(current=>current[nextIndex]!==undefined?current:[...current,state.nextCursor]);
    setPageIndex(nextIndex);setSelected(null);
  }
  function previousPage():void{if(pageIndex===0)return;setState(current=>({...current,loading:true,error:null}));setPageIndex(current=>current-1);setSelected(null);}
  function selectItem(item:InventoryItem):void{
    setMediaUrl(current=>{if(current!==null)URL.revokeObjectURL(current);return null;});
    setAuthority(null);setAuthorityLoading(true);
    setSelected(item);setRequestOpen(false);setAuthorizationDraft('');setNotice(null);
  }

  async function submitAuthorizationRequest(event:SyntheticEvent<HTMLFormElement>):Promise<void>{
    event.preventDefault();
    if(selected===null||authorizationBusy)return;
    setAuthorizationBusy(true);
    const result=await gateway.requestAuthorization(selected.listingId,authorizationDraft.trim()===''?null:authorizationDraft);
    setAuthorizationBusy(false);
    if(!result.ok){setState(current=>({...current,error:result.message}));return;}
    setAuthority({canShare:false,canRequest:false,authorityState:'pending',pendingRequestId:result.requestId});
    setRequestOpen(false);
    setNotice('Solicitud creada en Enviadas. El borrador quedó guardado y no se envió automáticamente.');
  }

  async function shareSelected():Promise<void>{
    if(selected===null||authority?.canShare!==true)return;
    const url=new URL(window.location.href);
    url.searchParams.set('listing',selected.listingId);
    const shareData={title:selected.title,text:selected.title,url:url.toString()};
    try{
      if(typeof navigator.share==='function'){await navigator.share(shareData);return;}
      await navigator.clipboard.writeText(url.toString());
      setNotice('Enlace de la propiedad copiado.');
    }catch{
      setState(current=>({...current,error:'No fue posible abrir el sistema para compartir.'}));
    }
  }

  async function copySelectedLink():Promise<void>{
    if(selected===null||authority?.canShare!==true)return;
    const url=new URL(window.location.href);
    url.searchParams.set('listing',selected.listingId);
    try{
      await navigator.clipboard.writeText(url.toString());
      setNotice('Enlace de la propiedad copiado.');
    }catch{
      setState(current=>({...current,error:'No fue posible copiar el enlace.'}));
    }
  }

  async function uploadPhoto(event:SyntheticEvent<HTMLInputElement>):Promise<void>{
    if(selected===null)return;const file=event.currentTarget.files?.[0]??null;event.currentTarget.value='';if(file===null)return;
    setUploadingMedia(true);setState(current=>({...current,error:null}));
    try{
      const result=await gateway.uploadPhoto(selected.listingId,file,selected.title);
      if(!result.ok){setState(current=>({...current,error:result.message}));return;}
      setMediaUrl(current=>{if(current!==null)URL.revokeObjectURL(current);return result.objectUrl;});
      const update=(candidate:InventoryItem):InventoryItem=>candidate.listingId===selected.listingId?{...candidate,mediaPath:result.mediaPath}:candidate;
      setState(current=>({...current,items:current.items.map(update)}));setMapItems(current=>current.map(update));setSelected(current=>current===null?null:update(current));
    }finally{setUploadingMedia(false);}
  }
  async function toggleFavorite(item:InventoryItem):Promise<void>{
    const result=await gateway.setFavorite(item.listingId,!item.isFavorite);
    if(!result.ok){setState(current=>({...current,error:result.message}));return;}
    const update=(candidate:InventoryItem):InventoryItem=>candidate.listingId===item.listingId?{...candidate,isFavorite:result.favorite}:candidate;
    setState(current=>({...current,items:current.items.map(update),error:null}));setMapItems(current=>current.map(update));setSelected(current=>current===null?null:update(current));
  }

  return <main className={styles.shell}>
    <header className={styles.appBar}>
      <button type="button" className={styles.brand} onClick={navigateToDashboard}><strong>Alpha•MLS</strong><span>• GLOBAL</span></button>
      <div className={styles.appActions}><button type="button" onClick={navigateToDashboard}>←</button><button type="button" onClick={navigateToDashboard}>⌂ <span>Inicio</span></button><div><small>ROL ACTIVO</small><strong>{state.identity?.displayName??'Sesión protegida'}</strong></div><button type="button" aria-label="Menú">☰</button></div>
    </header>

    <section className={styles.searchRow}>
      <button type="button" onClick={navigateToDashboard}>←</button>
      <form onSubmit={applySearch}><label htmlFor="inventory-search">Buscar</label><span>⌕</span><input id="inventory-search" value={queryDraft} onChange={event=>setQueryDraft(event.currentTarget.value)} placeholder="Dirección, zona, inmueble o tipo" autoComplete="off"/></form>
      <button type="button" aria-label="Acciones">⌂</button>
    </section>

    <nav className={styles.surfaceTabs}><a href="/workspaces">Deal<strong>Board</strong></a><button type="button" aria-current="page">Inventario <strong>MLS</strong></button></nav>

    <section className={styles.toolbar}>
      <div className={styles.filterGroup}>
        <button type="button" aria-pressed={filters.sale} className={filters.sale?styles.filterActive:styles.filter} onClick={()=>toggleTransaction('sale')}>Venta</button>
        <span>＋</span>
        <button type="button" aria-pressed={filters.rent} className={filters.rent?styles.filterActive:styles.filter} onClick={()=>toggleTransaction('rent')}>Renta</button>
        <div className={styles.statusGroup} aria-label="Estado del listado">
          <button type="button" aria-pressed={filters.active} className={filters.active?styles.filterActive:styles.filter} onClick={()=>toggleStatus('active')}>Activo</button>
          <button type="button" aria-pressed={filters.reserved} className={filters.reserved?styles.filterActive:styles.filter} onClick={()=>toggleStatus('reserved')}>Reservado</button>
          <button type="button" aria-pressed={filters.draft} className={filters.draft?styles.filterActive:styles.filter} onClick={()=>toggleStatus('draft')}>Borrador</button>
        </div>
        <div className={styles.zoneControls}>
          <button
            type="button"
            aria-pressed={drawingZone}
            className={drawingZone?styles.filterActive:styles.filter}
            onClick={()=>{setView('map');setDrawingZone(current=>!current);}}
          >
            {drawingZone?'Traza sobre el mapa':'Dibujar zona'}
          </button>
          {filters.zone!==null?<button type="button" className={styles.lightAction} onClick={clearZone}>Limpiar zona</button>:null}
        </div>
        <strong>{state.totalCount.toLocaleString('es-MX')} <small>propiedades</small></strong>
        <a className={styles.lightAction} href="/network#collections">Colecciones</a>
        <button type="button" className={styles.addListing} disabled>Agregar listado</button>
      </div>
      <div className={styles.viewToggle}><button type="button" aria-pressed={view==='map'} onClick={()=>setView('map')}>Mapa</button><button type="button" aria-pressed={view==='list'} onClick={()=>setView('list')}>Lista</button></div>
    </section>

    {state.error!==null?<div className={styles.error} role="alert">{state.error}</div>:null}
    {notice!==null?<div className={styles.notice} role="status"><span>{notice}</span><button type="button" onClick={()=>setNotice(null)}>×</button></div>:null}

    {view==='map'?<section className={styles.mapExperience}>
      <div className={styles.mapCanvas}>
        <RasterMap
          key={selected?.listingId??'inventory-map'}
          points={mapPoints}
          drawing={drawingZone}
          zone={filters.zone??[]}
          onDrawComplete={finishZone}
          onViewportChange={handleViewportChange}
          onSelect={listingId=>{const item=mapSource.find(candidate=>candidate.listingId===listingId);if(item!==undefined)selectItem(item);}}
          ariaLabel="Mapa del Inventario MLS"
          initialLatitude={selected?.latitude??19.4326}
          initialLongitude={selected?.longitude??-99.166}
          initialZoom={selected===null?13:15}
        />
        {mapPoints.length===0&&!mapLoading&&!state.loading?<div className={styles.mapEmpty}>No hay ubicaciones para estos filtros en esta vista.</div>:null}
        <div className={styles.viewportNotice}>{drawingZone?'Traza una zona cerrada con el dedo o puntero. El scroll vertical queda bloqueado sólo durante el trazo.':mapLoading?'Actualizando esta zona…':`${mapTotalCount.toLocaleString('es-MX')} resultados en esta vista`} {drawingZone?'':'· mueve o cambia el zoom para actualizar'}</div>
      </div>
      <div className={styles.propertyCarousel} ref={carouselRef}>{mapSource.slice(0,8).map(item=><PropertyCard key={item.listingId} item={item} selected={selected?.listingId===item.listingId} onSelect={()=>selectItem(item)} onFavorite={()=>void toggleFavorite(item)}/>)}</div>
    </section>:<section className={styles.listExperience}>
      <header><div><strong>{state.totalCount.toLocaleString('es-MX')} resultados</strong><span>Página {pageIndex+1}</span></div><label>Resultados por página<select value={pageSize} onChange={event=>changePageSize(Number(event.currentTarget.value) as PageSize)}><option value={10}>10</option><option value={20}>20</option><option value={30}>30</option></select></label></header>
      {state.loading?<div className={styles.loading} role="status">Leyendo inventario autorizado…</div>:null}
      {!state.loading&&state.items.length===0?<div className={styles.empty}>Sin coincidencias para estos filtros.</div>:null}
      <div className={styles.listGrid}>{state.items.map(item=><PropertyCard key={item.listingId} item={item} selected={selected?.listingId===item.listingId} onSelect={()=>selectItem(item)} onFavorite={()=>void toggleFavorite(item)}/>)}</div>
      <footer className={styles.pagination}><button type="button" onClick={previousPage} disabled={pageIndex===0}>← Anterior</button><span>{pageIndex+1}</span><button type="button" onClick={nextPage} disabled={state.nextCursor===null}>Siguiente →</button></footer>
    </section>}

    {selected!==null?<aside className={styles.detail}>
      <header><div><p>DETALLE</p><h2>{selected.title}</h2></div><button type="button" onClick={()=>{setSelected(null);setAuthority(null);setAuthorityLoading(false);setRequestOpen(false);}} aria-label="Cerrar detalle">×</button></header>
      <div className={styles.mediaPreview}>{mediaUrl===null?<div><strong>Storage privado</strong><span>{selected.mediaPath===null?'Aún no hay imagen real.':'El registro existe, pero el objeto todavía no está disponible.'}</span></div>:<img src={mediaUrl} alt={selected.title}/>}</div>
      <label className={styles.upload}>{uploadingMedia?'Subiendo imagen…':'Subir imagen real'}<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={uploadingMedia} onChange={event=>void uploadPhoto(event)}/></label>
      <strong className={styles.detailPrice}>{formatPrice(selected)}</strong>
      <dl><div><dt>Operación</dt><dd>{transactionLabel(selected.transactionType)}</dd></div><div><dt>Tipo</dt><dd>{propertyTypeLabel(selected.propertyType)}</dd></div><div><dt>Agency</dt><dd>{selected.agencyName}</dd></div><div><dt>Ubicación</dt><dd>{[selected.addressLine1,selected.neighborhood,selected.city,selected.state].filter(Boolean).join(', ')}</dd></div></dl>
      <button type="button" className={styles.detailFavorite} onClick={()=>void toggleFavorite(selected)}>{selected.isFavorite?'Quitar de Favoritos':'Agregar a Favoritos'}</button>
      <section className={styles.detailAuthority}>
        <small>AUTORIDAD DE COLABORACIÓN</small>
        {authorityLoading?<span>Verificando permisos…</span>:null}
        {!authorityLoading&&authority?.authorityState==='pending'?<button type="button" className={styles.authorityPending} disabled>Solicitud pendiente</button>:null}
        {!authorityLoading&&authority?.canRequest===true?<button type="button" className={styles.authorityButton} onClick={()=>setRequestOpen(true)}>Solicitar autorización</button>:null}
        {!authorityLoading&&authority?.canShare===true?<div className={styles.shareActions}>
          <button type="button" onClick={()=>void shareSelected()}>Compartir</button>
          <button type="button" onClick={()=>void copySelectedLink()}>Copiar enlace</button>
        </div>:null}
        {!authorityLoading&&authority?.authorityState==='represented'?<span>Representación propia activa.</span>:null}
        {!authorityLoading&&authority?.authorityState==='authorized'?<span>Autorización de colaboración activa.</span>:null}
      </section>
    </aside>:null}

    {selected!==null&&requestOpen?<div className={styles.requestBackdrop}>
      <form className={styles.requestPanel} onSubmit={(event)=>void submitAuthorizationRequest(event)}>
        <header><div><small>DEALBOARD · ENVIADAS</small><h2>Solicitar autorización</h2><p>{selected.title}</p></div><button type="button" onClick={()=>setRequestOpen(false)}>×</button></header>
        <p>La solicitud crea o reutiliza el WorkSpace, crea el Property Vector y deja un borrador editable. El mensaje no se envía automáticamente.</p>
        <label htmlFor="authorization-draft">Borrador para el listing agent</label>
        <textarea id="authorization-draft" rows={5} maxLength={4000} value={authorizationDraft} onChange={event=>setAuthorizationDraft(event.currentTarget.value)} placeholder="Escribe el contexto de la solicitud. Puedes dejarlo vacío."/>
        <footer><span>BusinessCard y procedencia quedan asociados a la relación.</span><button type="submit" disabled={authorizationBusy}>{authorizationBusy?'Creando…':'Crear solicitud'}</button></footer>
      </form>
    </div>:null}
  </main>;
}

function PropertyCard({item,selected,onSelect,onFavorite}:{readonly item:InventoryItem;readonly selected:boolean;readonly onSelect:()=>void;readonly onFavorite:()=>void}):React.JSX.Element{
  return <article className={selected?styles.cardSelected:styles.card} data-listing-id={item.listingId}>
    <button type="button" className={styles.cardMain} aria-label={`Abrir ${item.title}`} onClick={onSelect}>
      <div className={styles.photo}><span>{propertyTypeLabel(item.propertyType)}</span><b>{item.mediaPath===null?'1 / 1':'1 / 4'}</b></div>
      <div className={styles.cardCopy}><b className={styles.cardTitle}>{item.title}</b><strong>{formatPrice(item)}</strong><span>{propertyTypeLabel(item.propertyType)}</span><em>{transactionLabel(item.transactionType)}</em><p>{[item.addressLine1,item.neighborhood,item.city].filter(Boolean).join(', ')}</p><small>{[item.builtAreaM2===null?null:`${String(item.builtAreaM2)} m²`,item.parkingSpaces===null?null:`${String(item.parkingSpaces)} estac.`].filter(Boolean).join(' · ')}</small><i>{item.agencyName}</i></div>
    </button>
    <button type="button" className={styles.favorite} aria-label={item.isFavorite?`Quitar ${item.title} de Favoritos`:`Agregar ${item.title} a Favoritos`} onClick={onFavorite}>{item.isFavorite?'♥':'♡'}</button>
  </article>;
}
